<template>
  <q-dialog v-model="show" data-testid="1on1-dialog" persistent>
    <div class="oneOnOne-dialog">
      <c-page-section squares>
        <q-card class="full-width">
          <q-card-section>
            <div class="heading">
              {{ title }}
              <q-icon name="close" class="close-icon" @click="closeDialog" />
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section>
            <div class="row">
              <div class="col q-mr-md">
                <rating-panel
                  v-model:bad="state.user.bad"
                  v-model:ok="state.user.ok"
                  v-model:great="state.user.great"
                  :title="t('user1on1s.userRating.title')"
                  :description="t('user1on1s.userRating.description')"
                  data-testid="user-feeling"
                  editable
                />
              </div>

              <div class="col q-ml-md">
                <rating-panel
                  v-model:bad="state.company.bad"
                  v-model:ok="state.company.ok"
                  v-model:great="state.company.great"
                  :title="t('user1on1s.companyRating.title')"
                  :description="t('user1on1s.companyRating.description')"
                  data-testid="company-rating"
                  editable
                  required
                />
              </div>
            </div>
            <div class="row q-mt-lg q-mb-md full-height">
              <div class="col-12">
                <q-checkbox
                  v-model="state.isFinancialMessage"
                  data-testid="is-financial-message-checkbox"
                  class="is-financial-message-checkbox"
                  name="is-financial-message-checkbox"
                  :label="t('user1on1s.dialog.isFinancialMessage')"
                  @update:model-value="onFinancialMessageChange"
                />
              </div>
            </div>
            <div class="row q-mt-lg q-mb-md">
              <div class="col">
                <div class="q-mb-sm">
                  {{ t('user1on1s.dialog.note') }}
                </div>
                <wysiwyg-editor-v2
                  ref="editor"
                  class="one-on-one-note"
                  :smaller-headers="true"
                  @ready="onEditorReady"
                />
              </div>
            </div>
            <common-alert
              v-if="!props.hasUserVision"
              type="warning"
              show
              hide-close-button
              class="q-mb-sm"
              data-testid="ai-check-no-vision-alert"
              translate-message="userGoals.createGoalDialog.aiCheckUnavailable"
            />
            <div class="row justify-end items-center q-mt-md">
              <q-btn
                :label="t('userGoals.createGoalDialog.aiCheck')"
                outline
                icon="psychology"
                style="height: 56px"
                class="ai-check-btn"
                data-testid="ai-check-btn"
                :disable="!props.hasUserVision"
                :loading="isValidating1on1"
                @click="onValidate1on1BtnClick"
              />
            </div>
          </q-card-section>
          <q-card-section v-if="validationResult">
            <ai-validation-result
              :score="validationResult.score"
              :issues="validationResult.issues"
              :suggestions="validationResult.suggestions"
              :score-title="t('user1on1s.dialog.scoreTitle')"
              :issues-title="t('user1on1s.dialog.issuesTitle')"
              :suggestions-title="t('user1on1s.dialog.suggestionsTitle')"
              score-title-class="text-h5"
              issues-title-class="text-h5"
              suggestions-title-class="text-h5"
            />
          </q-card-section>
          <q-separator />
          <q-card-actions>
            <c-dialog-form-btns
              :negative-label="t('cancelBtn')"
              :positive-label="t('submitBtn')"
              positive-data-testid="1on1-submit-button"
              @action:negative="closeDialog"
              @action:positive="onConfirm"
            ></c-dialog-form-btns>
          </q-card-actions>
        </q-card>
      </c-page-section>
    </div>
  </q-dialog>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CPageSection from '@/components/_common/PageSection.vue';
import CDialogFormBtns from '@/components/_common/DialogFormBtns.vue';
import { hubClient } from '@tfpl/api-client';
import RatingPanel from '@/components/_common/RatingPanel.vue';
import WysiwygEditorV2 from '@/components/_common/WysiwygEditorV2.vue';
import { useUser1on1 } from '@/composables/useUser1on1';
import AiValidationResult from '@/components/_common/AiValidationResult.vue';
import { useNotify, NotifyType } from '@/composables/useNotify';
import CommonAlert from '@/components/_common/CommonAlert.vue';

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = withDefaults(
  defineProps<{
    hasUserVision?: boolean;
  }>(),
  {
    hasUserVision: true,
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['saved']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/

const { t } = useI18n();
const show = ref(false);
const title = ref('');
const { create1on1, update1on1, validate1on1 } = useUser1on1();

/**
 * State
 */
const defaultState = {
  isNew: true,
  user: {
    bad: false,
    ok: false,
    great: false,
  },
  company: {
    bad: false,
    ok: false,
    great: false,
  },
  message: 'as',
  userUuid: null,
  isFinancialMessage: false,
};
const state = ref<{
  isNew: boolean;
  user: { bad: boolean; ok: boolean; great: boolean };
  company: { bad: boolean; ok: boolean; great: boolean };
  message: string | null;
  userUuid: string | null;
  uuid?: string | null;
  isFinancialMessage: boolean;
}>(defaultState);

const resetState = () => {
  state.value.company.bad = false;
  state.value.company.ok = false;
  state.value.company.great = false;

  state.value.user.bad = false;
  state.value.user.ok = false;
  state.value.user.great = false;

  state.value.message = '';
  state.value.userUuid = null;
  state.value.uuid = null;

  state.value.isFinancialMessage = false;
};

/**
 * Dialog open / close
 * @param userUuid
 * @param oneOnOne
 */
const openDialog = (userUuid: string, oneOnOne?: hubClient.Collaborator1On1Dto) => {
  resetState();
  state.value.userUuid = userUuid;
  if (oneOnOne) {
    title.value = t('user1on1s.dialog.edit');
    state.value.message = oneOnOne.message;
    state.value.uuid = oneOnOne.uuid;
    state.value.isFinancialMessage = oneOnOne.isFinancialMessage;
    state.value.isNew = false;
    mapCompanyRatingToState(oneOnOne);
    mapUserRatingToState(oneOnOne);
  } else {
    title.value = t('user1on1s.dialog.add');
    state.value.company.ok = true;
  }
  show.value = true;
};

const closeDialog = () => {
  show.value = false;
};

const mapCompanyRatingToState = (oneOneOn: hubClient.Collaborator1On1Dto) => {
  switch (oneOneOn.companyRating) {
    case hubClient.Collaborator1On1Rating.BAD:
      state.value.company.bad = true;
      break;
    case hubClient.Collaborator1On1Rating.OK:
      state.value.company.ok = true;
      break;
    case hubClient.Collaborator1On1Rating.GREAT:
      state.value.company.great = true;
      break;
    default:
      throw new Error('Unknown company rating');
  }
};

const mapUserRatingToState = (oneOneOn: hubClient.Collaborator1On1Dto) => {
  switch (oneOneOn.collaboratorRating) {
    case hubClient.Collaborator1On1Rating.BAD:
      state.value.user.bad = true;
      break;
    case hubClient.Collaborator1On1Rating.OK:
      state.value.user.ok = true;
      break;
    case hubClient.Collaborator1On1Rating.GREAT:
      state.value.user.great = true;
      break;
    case null:
    case undefined:
      break;
    default:
      throw new Error('Unknown user rating');
  }
};
const onConfirm = async () => {
  let companyRating: hubClient.Collaborator1On1Dto['companyRating'];
  if (state.value.company.bad) {
    companyRating = hubClient.Collaborator1On1Rating.BAD;
  } else if (state.value.company.ok) {
    companyRating = hubClient.Collaborator1On1Rating.OK;
  } else if (state.value.company.great) {
    companyRating = hubClient.Collaborator1On1Rating.GREAT;
  } else {
    throw new Error('Unknown company rating');
  }

  let collaboratorRating: hubClient.Collaborator1On1Dto['collaboratorRating'] | null = null;
  if (state.value.user.bad) {
    collaboratorRating = hubClient.Collaborator1On1Rating.BAD;
  } else if (state.value.user.ok) {
    collaboratorRating = hubClient.Collaborator1On1Rating.OK;
  } else if (state.value.user.great) {
    collaboratorRating = hubClient.Collaborator1On1Rating.GREAT;
  }

  if (state.value.uuid) {
    // edit
    await update1on1(state.value.uuid, {
      message: editor.value.getHTML(),
      companyRating,
      collaboratorRating,
      isFinancialMessage: state.value.isFinancialMessage,
    });
  } else {
    // create
    await create1on1({
      collaboratorUuid: state.value.userUuid as string,
      message: editor.value.getHTML(),
      date: new Date().toISOString(),
      companyRating,
      collaboratorRating,
      isFinancialMessage: state.value.isFinancialMessage,
    });
  }

  emit('saved');
  closeDialog();
};

const editor = ref();
const onEditorReady = () => {
  if (state.value.isNew) {
    setEditorTemplate();
  } else {
    editor.value.setHTML(state.value.message);
  }
};

const isValidating1on1 = ref(false);
const { notify } = useNotify();
const validationResult = ref<hubClient.Validate1On1ResponseDto | null>(null);

const getRatingString = (rating: { bad: boolean; ok: boolean; great: boolean }): string | null => {
  if (rating.bad) return 'bad';
  if (rating.ok) return 'ok';
  if (rating.great) return 'great';
  return null;
};

const onValidate1on1BtnClick = async () => {
  const plainText = editor.value?.getPlainText()?.trim();

  if (!plainText) {
    return;
  }

  const userRating = getRatingString(state.value.user);
  const companyRating = getRatingString(state.value.company);

  if (!companyRating) {
    notify(t('common.requestError'), NotifyType.ERROR);
    return;
  }

  isValidating1on1.value = true;
  validationResult.value = null;
  try {
    const response = await validate1on1(plainText, userRating, companyRating, state.value.isFinancialMessage);
    // Sort issues by severity (highest priority first)
    if (response && Array.isArray(response.issues)) {
      const orderMap: Record<hubClient.Validate1On1IssueDto['severity'], number> = {
        high: 3,
        medium: 2,
        low: 1,
      } as const;
      response.issues = [...response.issues].sort((a, b) => (orderMap[b.severity] ?? 0) - (orderMap[a.severity] ?? 0));
    }
    validationResult.value = response;
  } catch (e) {
    console.log(e);
    notify(t('common.requestError'), NotifyType.ERROR);
  } finally {
    isValidating1on1.value = false;
  }
};

const editorTemplates = `
    <p>[Povinné]</p>
    <h1>Co se řešilo</h1>
    <p>Jak se pracovník má? Co ho blokuje jak v pracovní tak osobní oblasti?</p>
    <ul><li></li></ul>
    <p>Co se povedlo? Konkrétní uznání</p>
    <ul><li></li></ul>
    <p><br></p>
    <p>[Volitelné]</p>
    <p><strong>Výstupy a následné akční kroky pro spolupracovníka</strong></p>
    <p>Co vyplynulo ze zpětné vazby na spolupracovníka</p>
    <ul><li></li></ul>
    <p>Akční kroky</p>
    <ul><li>Příprava na příští 1:1 (např. zpětná vazba, vlastní zhodnocení seberozvoje a produktivity).</li></ul>
    <ul><li></li></ul>
    <p><br></p>
    <p>[Volitelné]</p>
    <p><strong>Výstupy a následné kroky pro vedoucího</strong></p>
    <p>Co vyplynulo ze zpětné vazby na vedoucího</p>
    <ul><li></li></ul>
    <p>Akční kroky</p>
    <ul><li></li></ul>
    <p><br></p>
`;

const setEditorTemplate = () => {
  editor.value.setHTML(editorTemplates);
};

const financialMessageTemplate = `
    <p><br></p>
    <h1>Finanční poznámka</h1>
    <p><strong>Nová výše odměny</strong></p>
    <ul><li></li></ul>
    <p><strong>Odkdy je nová výše odměny platná</strong></p>
    <ul><li></li></ul>
    <p><br></p>
`;

const FINANCIAL_SECTION_MARKER = '<h1>Finanční poznámka</h1>';

const stripTags = (html: string): string => {
  // Equivalent to html.replaceAll(/<[^>]+>/g, ' ') without regex backtracking:
  // a match of /<[^>]+>/g can't cross a '>', so it lies entirely within one chunk
  // between two '>' characters and always ends at that '>'. Leftmost-first semantics
  // pick the first '<' in the chunk; the only case with no match is a '<' immediately
  // followed by '>' (the pattern needs at least one char between them), which this
  // leaves untouched together with the trailing '>'.
  const chunks = html.split('>');
  const rest = chunks.pop() ?? '';
  return (
    chunks
      .map((chunk) => {
        const tagStart = chunk.indexOf('<');
        return tagStart !== -1 && tagStart < chunk.length - 1 ? `${chunk.slice(0, tagStart)} ` : `${chunk}>`;
      })
      .join('') + rest
  );
};

const toCanonicalText = (html: string): string => {
  // Replace tags with spaces so block boundaries don't glue words together,
  // then decode HTML entities via a detached element, and collapse whitespace.

  const withoutTags = stripTags(html);
  const div = document.createElement('div');
  div.innerHTML = withoutTags;
  return (div.textContent ?? '').replaceAll(/\s+/g, ' ').trim();
};

const onFinancialMessageChange = (value: boolean) => {
  if (!editor.value) {
    return;
  }
  const currentHtml = editor.value.getHTML() ?? '';

  if (value) {
    if (currentHtml.includes(FINANCIAL_SECTION_MARKER)) {
      return;
    }
    editor.value.setHTML(currentHtml + financialMessageTemplate);
    return;
  }

  const markerIdx = currentHtml.indexOf(FINANCIAL_SECTION_MARKER);
  if (markerIdx === -1) {
    return;
  }

  const tailCanonical = toCanonicalText(currentHtml.slice(markerIdx));
  const templateCanonical = toCanonicalText(financialMessageTemplate);

  if (tailCanonical !== templateCanonical) {
    return;
  }

  // This non-capturing-group rewrite removes the superlinear backtracking risk (verified
  // equivalent to the previous pattern via a local jsdom-based script, see MR description),
  // but ESLint's sonarjs/regular-expr still flags any regex touching user-controlled HTML.
  // eslint-disable-next-line sonarjs/regular-expr
  const beforeHtml = currentHtml.slice(0, markerIdx).replace(/<p>\s*(?:<br\s*\/?>\s*)?<\/p>\s*$/, '');
  editor.value.setHTML(beforeHtml);
};
/*****************************************************************************
 * EXPOSE
 ****************************************************************************/
defineExpose({
  openDialog,
});
</script>
<style lang="scss" scoped>
@import 'styles/app.variables.scss';
.one-on-one-note {
  height: 100%;
}

.oneOnOne-dialog {
  max-width: 900px !important;
}

.ai-check-btn {
  padding-left: 15px;
  padding-right: 15px;
}
</style>
